import React, { useCallback, useEffect, useState } from 'react'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { marketingSolicitacaoService } from '../../services/marketingSolicitacao'
import type { MarketingRequestStage } from '../../types/marketingSolicitacao'
import {
  DEFAULT_STAGE_COLOR,
  PARAMS_MODULE_NAME,
  PERM_LOADING,
  SELECT_CLASS,
  slugifyStageKey,
} from './solicitacaoUi'

const SolicitacaoStagesTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const [loading, setLoading] = useState(true)
  const [stages, setStages] = useState<MarketingRequestStage[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<MarketingRequestStage> | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await marketingSolicitacaoService.listStages()
      setStages(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar etapas.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const openNew = () => {
    setEditing({
      name: '',
      order_index: (stages.length + 1) * 10,
      is_closed: false,
      color: null,
    })
    setError(null)
    setModalOpen(true)
  }

  const openEdit = (stage: MarketingRequestStage) => {
    setEditing({ ...stage })
    setError(null)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditing(null)
  }

  const save = async () => {
    if (!editing?.name?.trim()) {
      setError('Nome é obrigatório.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editing.id) {
        const updated = await marketingSolicitacaoService.updateStage(editing.id, {
          name: editing.name,
          order_index: Number(editing.order_index || 0),
          color: editing.color || null,
          is_closed: !!editing.is_closed,
        })
        void logUpdate(
          'marketing_request_stage',
          updated.id,
          `Atualizou etapa de solicitação de marketing: ${updated.name}`,
          {
            name: editing.name,
            order_index: editing.order_index,
            color: editing.color,
            is_closed: editing.is_closed,
          },
          {
            name: updated.name,
            order_index: updated.order_index,
            color: updated.color,
            is_closed: updated.is_closed,
          }
        )
      } else {
        const taken = new Set(stages.map((s) => s.key))
        const created = await marketingSolicitacaoService.createStage({
          name: editing.name,
          key: slugifyStageKey(editing.name.trim(), taken),
          order_index: Number(editing.order_index || 0),
          color: editing.color || null,
          is_closed: !!editing.is_closed,
        })
        void logCreate(
          'marketing_request_stage',
          created.id,
          `Criou etapa de solicitação de marketing: ${created.name}`,
          {
            name: created.name,
            key: created.key,
            order_index: created.order_index,
            is_closed: created.is_closed,
          }
        )
      }
      closeModal()
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar etapa.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (stage: MarketingRequestStage) => {
    if (
      !window.confirm(
        'Excluir esta etapa? A exclusão é lógica: deixa de aparecer no Kanban e na lista de etapas ativas, mas solicitações já vinculadas mantêm a referência.'
      )
    ) {
      return
    }
    try {
      await marketingSolicitacaoService.softDeleteStage(stage.id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão pela tela de parâmetros de marketing',
      })
      void logDelete(
        'marketing_request_stage',
        stage.id,
        `Excluiu etapa de solicitação de marketing: ${stage.name}`,
        { name: stage.name, key: stage.key }
      )
      await load()
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Não foi possível excluir esta etapa.')
    }
  }

  const displayColor = (stage: MarketingRequestStage) => stage.color || DEFAULT_STAGE_COLOR

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Etapas (Kanban)
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            Configure as etapas usadas na tela de solicitações de marketing.
          </Text>
        </div>
        <PermissionGuard
          moduleName={PARAMS_MODULE_NAME}
          permissionType="insert"
          loadingComponent={PERM_LOADING}
          fallback={null}
        >
          <Button variant="solid" onClick={openNew}>
            Nova etapa
          </Button>
        </PermissionGuard>
      </div>

      {error && !modalOpen && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Cor
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Nome
                </th>
                <th className="px-4 py-2 text-center text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Ordem
                </th>
                <th className="px-4 py-2 text-center text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Encerrado
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : stages.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Nenhuma etapa cadastrada.
                  </td>
                </tr>
              ) : (
                stages.map((stage) => (
                  <tr key={stage.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20">
                    <td className="px-4 py-2">
                      <span
                        className="inline-block h-4 w-4 rounded-full"
                        style={{ backgroundColor: displayColor(stage) }}
                        title={stage.color || 'Cor padrão'}
                      />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{stage.name}</td>
                    <td className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">
                      {stage.order_index}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          stage.is_closed
                            ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            : 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                        }`}
                      >
                        {stage.is_closed ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard
                          moduleName={PARAMS_MODULE_NAME}
                          permissionType="update"
                          loadingComponent={PERM_LOADING}
                          fallback={null}
                        >
                          <button
                            type="button"
                            title="Editar"
                            onClick={() => openEdit(stage)}
                            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-gray-700"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                        <PermissionGuard
                          moduleName={PARAMS_MODULE_NAME}
                          permissionType="delete"
                          loadingComponent={PERM_LOADING}
                          fallback={null}
                        >
                          <button
                            type="button"
                            title="Excluir"
                            onClick={() => void remove(stage)}
                            className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
            <div className="mb-4 flex items-center justify-between">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {editing.id ? 'Editar etapa' : 'Nova etapa'}
              </Text>
              <Button variant="ghost" onClick={closeModal} disabled={saving}>
                Fechar
              </Button>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Text size="sm" className="mb-1 text-gray-700 dark:text-gray-300">
                  Nome de exibição *
                </Text>
                <Input
                  placeholder="ex: Em andamento"
                  value={editing.name || ''}
                  onChange={(e) => setEditing((prev) => ({ ...prev!, name: e.target.value }))}
                />
                <Text size="xs" color="muted" className="mt-1">
                  {editing.id
                    ? 'A chave interna não é alterada ao editar o nome.'
                    : 'A chave interna será gerada automaticamente a partir do nome.'}
                </Text>
              </div>

              <div>
                <Text size="sm" className="mb-1 text-gray-700 dark:text-gray-300">
                  Ordem
                </Text>
                <input
                  type="number"
                  min={0}
                  value={editing.order_index ?? ''}
                  onChange={(e) =>
                    setEditing((prev) => ({
                      ...prev!,
                      order_index: e.target.value !== '' ? Number(e.target.value) : 0,
                    }))
                  }
                  className={SELECT_CLASS + ' w-full'}
                />
              </div>

              <div>
                <Text size="sm" className="mb-2 text-gray-700 dark:text-gray-300">
                  Cor da etapa
                </Text>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={editing.color || DEFAULT_STAGE_COLOR}
                    onChange={(e) => setEditing((prev) => ({ ...prev!, color: e.target.value }))}
                    className="h-10 w-14 cursor-pointer rounded border border-gray-300 bg-white p-0.5 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <input
                    type="text"
                    maxLength={7}
                    placeholder="#6b7280"
                    value={editing.color || ''}
                    onChange={(e) => {
                      const val = e.target.value
                      if (val === '' || /^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                        setEditing((prev) => ({ ...prev!, color: val || null }))
                      }
                    }}
                    className="w-32 rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  />
                  <span
                    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white"
                    style={{ backgroundColor: editing.color || DEFAULT_STAGE_COLOR }}
                  >
                    {editing.name || 'Prévia'}
                  </span>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={editing.is_closed ?? false}
                  onChange={(e) => setEditing((prev) => ({ ...prev!, is_closed: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Etapa de encerramento
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void save()} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SolicitacaoStagesTab
